import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BORNES_BULLE } from '@nextilt/commun';
import { api } from '../partage/api/client';
import type { NoteDeLaBoite } from '../partage/api/types';
import { cles } from '../partage/relectures';
import { useAuPouce } from '../partage/surface';
import { useDossiers } from '../partage/useArbre';
import { useMenuOuvert } from '../partage/useMenuOuvert';
import {
  AUCUN_PROJET_OU_RANGER,
  BOITE_D_ENTREE,
  BOITE_VIDE,
  CHERCHER_OU_RANGER,
  MES_PROJETS_POUR_RANGER,
  NOTER,
  NOTER_DANS_LA_BOITE,
  RANGER_DANS,
  RETIRER_LA_NOTE,
  RIEN_DE_CE_NOM,
  SOUS_TITRE_DE_LA_BOITE,
  UN_PROJET,
  mentionDeLaNote,
} from '../partage/vocabulaire';
import { chercheParTitre, choixDeRangement } from './reglesDeLaBoite';
import { useBoite } from './useBoite';
import './BoiteDEntree.css';

/**
 * La page Boîte d'entrée de l'ordinateur (chantier boite-d-entree, ticket 02). Aucune maquette :
 * l'habit carnet des autres pages (la tête et la feuille du Profil). En haut, la case « Noter… »,
 * Entrée ajoute ; dessous, les notes, la plus récente en haut, chacune avec sa date courte, « par
 * Claude » quand elle vient de lui, « Ranger dans… » et la croix. Vide, une phrase dit à quoi
 * elle sert.
 *
 * Au téléphone, pas de page (chantier en attente `telephone-carnet`) : l'adresse ramène à
 * Maintenant, comme le Profil.
 */
export function BoiteDEntree() {
  const auPouce = useAuPouce();
  const boite = useBoite();
  if (auPouce) return <Navigate to="/maintenant" replace />;

  return (
    <section className="boite">
      <header className="boite-haut">
        <h1>{BOITE_D_ENTREE}</h1>
        <p>{SOUS_TITRE_DE_LA_BOITE}</p>
      </header>

      <div className="boite-feuille">
        <CaseNoter onNoter={boite.noter} />
        {boite.annonce && (
          <p className="boite-annonce" role="status">
            {boite.annonce}
          </p>
        )}
        {boite.notes !== null &&
          (boite.notes.length === 0 ? (
            <p className="boite-vide">{BOITE_VIDE}</p>
          ) : (
            <ul className="boite-notes">
              {boite.notes.map((n) => (
                <Note key={n.id} note={n} onRanger={(mereId) => boite.ranger(n.id, mereId)} onRetirer={() => boite.retirer(n.id)} />
              ))}
            </ul>
          ))}
      </div>
    </section>
  );
}

/** La case « Noter… » : Entrée ajoute la note, la case se vide et reste prête pour la suivante. */
function CaseNoter({ onNoter }: { onNoter: (ecrit: string) => Promise<boolean> }) {
  const [ecrit, setEcrit] = useState('');
  const [enCours, setEnCours] = useState(false);
  return (
    <form
      className="boite-noter"
      onSubmit={(e) => {
        e.preventDefault();
        if (enCours) return;
        setEnCours(true);
        void onNoter(ecrit).then((notee) => {
          setEnCours(false);
          if (notee) setEcrit('');
        });
      }}
    >
      <input
        value={ecrit}
        placeholder={NOTER}
        aria-label={NOTER_DANS_LA_BOITE}
        maxLength={BORNES_BULLE.titreMax}
        autoFocus
        onChange={(e) => setEcrit(e.target.value)}
      />
    </form>
  );
}

/** Une note : son texte, sa mention, « Ranger dans… » et la croix. */
function Note({
  note,
  onRanger,
  onRetirer,
}: {
  note: NoteDeLaBoite;
  onRanger: (mereId: string) => Promise<void>;
  onRetirer: () => void;
}) {
  return (
    <li className="boite-note" data-note={note.id}>
      <div className="boite-note-corps">
        <span className="boite-note-texte">{note.texte}</span>
        <small>{mentionDeLaNote(note)}</small>
      </div>
      <RangerDans onRanger={onRanger} />
      <button type="button" className="boite-retirer" aria-label={RETIRER_LA_NOTE} title={RETIRER_LA_NOTE} onClick={onRetirer}>
        ×
      </button>
    </li>
  );
}

/**
 * « Ranger dans… » : un menu qui cherche. Ouvert, il montre tes projets ; un mot tapé (deux signes
 * au moins) cherche une bulle par son titre dans tous les projets, avec son chemin. Un choix range
 * la note ; Entrée prend le premier. Un clic ailleurs ou Échap le referme (`useMenuOuvert`).
 */
function RangerDans({ onRanger }: { onRanger: (mereId: string) => Promise<void> }) {
  const menu = useMenuOuvert();
  const [texte, setTexte] = useState('');
  const [enCours, setEnCours] = useState(false);
  const dossiers = useDossiers();
  const cherche = chercheParTitre(texte);
  const recherche = useQuery({
    queryKey: cles.recherche(texte),
    queryFn: () => api.rechercher(texte),
    enabled: menu.ouvert && cherche,
  });
  const choix = choixDeRangement(texte, dossiers.data?.dossiers ?? [], recherche.data?.resultats ?? []);
  const attendLaRecherche = cherche && recherche.isPending;

  const ranger = (mereId: string) => {
    if (enCours) return;
    setEnCours(true);
    void onRanger(mereId).finally(() => {
      setEnCours(false);
      menu.setOuvert(false);
      setTexte('');
    });
  };

  return (
    <div className="boite-ranger" ref={menu.ref}>
      <button
        type="button"
        className="boite-ranger-bouton"
        aria-expanded={menu.ouvert}
        onClick={() => {
          setTexte('');
          menu.setOuvert(!menu.ouvert);
        }}
      >
        {RANGER_DANS}
      </button>
      {menu.ouvert && (
        <div className="boite-ranger-menu" role="dialog" aria-label={RANGER_DANS}>
          <input
            value={texte}
            placeholder={CHERCHER_OU_RANGER}
            aria-label={CHERCHER_OU_RANGER}
            autoFocus
            disabled={enCours}
            onChange={(e) => setTexte(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (choix[0]) ranger(choix[0].id);
              }
            }}
          />
          {!cherche && <p className="boite-ranger-aide">{MES_PROJETS_POUR_RANGER}</p>}
          {!attendLaRecherche && choix.length === 0 && (
            <p className="boite-ranger-rien">{cherche ? RIEN_DE_CE_NOM : AUCUN_PROJET_OU_RANGER}</p>
          )}
          {choix.length > 0 && (
            <ul className="boite-ranger-choix">
              {choix.map((c) => (
                <li key={c.id}>
                  <button type="button" disabled={enCours} data-choix={c.id} onClick={() => ranger(c.id)}>
                    <span className="boite-ranger-titre">{c.titre}</span>
                    <small>{c.ou.length > 0 ? c.ou.join(' › ') : UN_PROJET}</small>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
